/**
 * React Router v7 Route Configuration
 *
 * Defines all client-side routes for the FindOp Console SPA.
 * Replaces Next.js App Router file-system based routing.
 */

import { createBrowserRouter, Navigate } from "react-router-dom";

// Layouts
import { AuthLayout } from "@/layouts/AuthLayout";
import { DashboardLayout } from "@/layouts/DashboardLayout";
import { SettingsLayout } from "@/layouts/SettingsLayout";

// Guards
import { AuthGuard } from "@/components/AuthGuard";

// Auth Pages
import { LoginPage } from "@/pages/auth/Login";
import { RegisterPage } from "@/pages/auth/Register";
import { ForgotPasswordPage } from "@/pages/auth/ForgotPassword";
import { AuthCallbackPage } from "@/pages/auth/Callback";

// Dashboard Pages
import { DashboardOverviewPage } from "@/pages/dashboard/Overview";
import { ContentPage } from "@/pages/dashboard/Content";
import { ContentNewPage } from "@/pages/dashboard/ContentNew";
import { ContentDetailPage } from "@/pages/dashboard/ContentDetail";
import AnalyticsOverviewPage from "@/pages/dashboard/analytics/AnalyticsOverview";
import PerformanceAnalyticsPage from "@/pages/dashboard/analytics/AnalyticsPerformance";
import RevenueAnalyticsPage from "@/pages/dashboard/analytics/AnalyticsRevenue";

// Settings Pages
import { ProfileSettingsPage } from "@/pages/dashboard/settings/Profile";
import { BillingSettingsPage } from "@/pages/dashboard/settings/Billing";
import { TeamSettingsPage } from "@/pages/dashboard/settings/Team";
import { ApiKeysSettingsPage } from "@/pages/dashboard/settings/ApiKeys";

// Home Page
import { HomePage } from "@/pages/Home";

export const router = createBrowserRouter([
  // Public routes
  {
    path: "/",
    element: <HomePage />,
  },

  // Auth callback (no layout)
  {
    path: "/auth/callback",
    element: <AuthCallbackPage />,
  },

  // Auth routes (public, with auth layout)
  {
    element: <AuthLayout />,
    children: [
      { path: "/login", element: <LoginPage /> },
      { path: "/register", element: <RegisterPage /> },
      { path: "/forgot-password", element: <ForgotPasswordPage /> },
    ],
  },

  // Protected routes (require auth)
  {
    element: <AuthGuard />,
    children: [
      {
        element: <DashboardLayout />,
        children: [
          // Dashboard
          { path: "/dashboard", element: <DashboardOverviewPage /> },

          // Content
          { path: "/content", element: <ContentPage /> },
          { path: "/content/new", element: <ContentNewPage /> },
          { path: "/content/:id", element: <ContentDetailPage /> },

          // Analytics
          { path: "/analytics", element: <AnalyticsOverviewPage /> },
          {
            path: "/analytics/performance",
            element: <PerformanceAnalyticsPage />,
          },
          { path: "/analytics/revenue", element: <RevenueAnalyticsPage /> },

          // Settings (nested layout)
          {
            element: <SettingsLayout />,
            children: [
              {
                path: "/settings",
                element: <Navigate to="/settings/profile" replace />,
              },
              { path: "/settings/profile", element: <ProfileSettingsPage /> },
              { path: "/settings/billing", element: <BillingSettingsPage /> },
              { path: "/settings/team", element: <TeamSettingsPage /> },
              { path: "/settings/api-keys", element: <ApiKeysSettingsPage /> },
            ],
          },
        ],
      },
    ],
  },

  // Catch-all: redirect to dashboard
  {
    path: "*",
    element: <Navigate to="/dashboard" replace />,
  },
]);
